import React, { useEffect, useState } from "react";
import { Cabinet } from "@/components/Cabinet";
import { Starfield } from "@/components/Starfield";
import { PixelSprite } from "@/components/PixelSprite";
import { logout, getLeaderboard } from "@/game/api";
import { useLoginForm } from "@/game/useLoginForm";
import { warn } from "@/game/log";
import { User, Rocket } from "lucide-react";
import { ModeToggle, LevelSelect, PlayerStats, Leaderboard } from "@/components/StartBits";

function LoginForm({ onLoggedIn }) {
  const { name, setName, password, setPassword, error, loading, canSubmit, submit } = useLoginForm(onLoggedIn);

  return (
    <form onSubmit={submit} className="w-full max-w-sm flex flex-col items-center gap-4 mt-4">
      <label className="font-arcade text-[10px] text-slate-400 tracking-widest">PILOT LOGIN</label>
      <input
        data-testid="player-name-input"
        autoFocus
        value={name}
        maxLength={32}
        autoComplete="username"
        onChange={(e) => setName(e.target.value)}
        placeholder="USERNAME"
        className="w-full bg-[#090914] border-2 border-[#00f0ff] text-cyan-300 text-center font-typewriter text-lg rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#ff007f] placeholder-slate-600 tracking-widest"
      />
      <input
        data-testid="player-password-input"
        type="password"
        value={password}
        maxLength={64}
        autoComplete="current-password"
        onChange={(e) => setPassword(e.target.value)}
        placeholder="PASSWORD"
        className="w-full bg-[#090914] border-2 border-[#ffb000] text-[#ffb000] text-center font-typewriter text-lg rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#ff007f] placeholder-slate-600 tracking-[0.3em]"
      />
      {error && (
        <p data-testid="login-error" className="font-vt text-lg text-[#ff2a2a] text-center">
          {error}
        </p>
      )}
      <button
        data-testid="enter-arcade-btn"
        type="submit"
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-2 bg-[#1c1a36] border-2 border-[#39ff14] text-[#39ff14] hover:bg-[#39ff14] hover:text-[#06060e] hover:shadow-[0_0_18px_#39ff14] active:translate-y-1 transition-all uppercase tracking-widest font-arcade text-xs py-3 rounded-lg disabled:opacity-40"
      >
        <Rocket size={16} /> {loading ? "CHECKING..." : "Log In & Play"}
      </button>
      <p className="font-vt text-base text-slate-500 text-center">
        Your username and password were sent to you. Ask a grown-up if you lost them.
      </p>
    </form>
  );
}

function Logo() {
  return (
    <div className="text-center mb-6 tb-float">
      <div className="flex items-center justify-center gap-3 mb-3">
        <PixelSprite id="alienlogo" color="#39ff14" px={3} />
        <h1 className="font-arcade text-2xl sm:text-4xl text-cyan-400 text-glow-cyan tracking-wider">
          TYPE<span className="text-[#ff007f] text-glow-pink">BLASTER</span>
        </h1>
        <PixelSprite id="xyz" color="#39ff14" px={3} />
      </div>
      <p className="font-vt text-xl sm:text-2xl text-emerald-300">Shoot the aliens by typing their labels!</p>
    </div>
  );
}

export function StartScreen({ player, setPlayer, onStart, settings }) {
  const [board, setBoard] = useState([]);
  const [practice, setPractice] = useState(false);

  useEffect(() => {
    if (!player) {
      setBoard([]);
      return undefined;
    }
    let cancelled = false;
    getLeaderboard()
      .then((rows) => !cancelled && setBoard(rows))
      .catch((err) => warn("Leaderboard unavailable:", err?.message));
    return () => {
      cancelled = true;
    };
  }, [player]);

  const handleLogout = async () => {
    setPlayer(null);
    await logout();
  };

  return (
    <Cabinet settings={settings}>
      <Starfield />
      <div className="relative z-10 h-full overflow-y-auto flex flex-col items-center px-4 sm:px-8 py-6">
        <Logo />
        {!player ? (
          <LoginForm onLoggedIn={setPlayer} />
        ) : (
          <div className="w-full max-w-2xl flex flex-col items-center gap-6">
            <div className="flex items-center gap-2 font-vt text-2xl text-cyan-300">
              <User size={20} className="text-[#ff007f]" />
              <span data-testid="player-greeting">{player.name}</span>
              <button
                data-testid="change-player-btn"
                onClick={handleLogout}
                className="ml-3 text-xs font-typewriter text-slate-400 border border-[#3a366b] rounded px-2 py-1 hover:text-cyan-300"
              >
                log out
              </button>
            </div>
            <ModeToggle practice={practice} setPractice={setPractice} />
            <LevelSelect player={player} practice={practice} onStart={onStart} />
            <PlayerStats player={player} />
          </div>
        )}
        <Leaderboard board={board} />
      </div>
    </Cabinet>
  );
}
